﻿import { Registration, SaveSpeaker, SendSpeakerUploadedDetailsEmail, UserDto } from '@/api/dtos';
import Loading from '@/components/common/Loading';
import AccommodationSectionUpdate from '@/components/speaker/SpeakerAccommodationSection';
import SpeakerGuideSection from '@/components/speaker/SpeakerGuideSection';
import SpeakerSection from '@/components/speaker/SpeakerSection';
import SpeakerTravelSection from '@/components/speaker/SpeakerTravelSection';
import useSpeakerByGuid from '@/hooks/useSpeakerByGuid';
import { useFormSubmitValidationFailed } from '@/hooks/useFormSubmitValidationFailed';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Paper, Typography, styled } from '@mui/material';
import { AuthenticatedUserContext, Form, FormSubmitButton, FormsFeatureContext, IValidationError, useFormLookups, useFormMetadata, useFormValidate } from '@ngt/forms';
import { IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import { FC, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import speakerBannerImg from '@/assets/img/speaker-banner-2026.png';
import SessionsSectionUpdate from './SpeakerPackages';
import PackageProvider from '../registration/packages/PackageProvider';
import { AdminContext } from '@/contexts/AdminContext';
import { immerable } from 'immer';


export const StyledAccordianSummary = styled(AccordionSummary)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    borderRadius: "4px",

}))

export const StyledAccordion = styled(Accordion)(({theme}) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}))

export const StyledAccordionDetails = styled(AccordionDetails)(({theme}) => ({
    padding: theme.spacing(3),
}))


const StyledTypography = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(2),
}))

const BannerImage = styled('img')(({theme}) => ({
    width: '100%',
    maxHeight: '600px',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    marginBottom: '0px !important',
    paddingBottom: '0px !important'
}))

const ContentBox = styled(Box)(({theme}) => ({
    paddingLeft: '100px',
    paddingRight: '100px',
    paddingTop: '0px !important',
    marginTop: '0px !important',
    paddingBottom:'30px',

    [theme.breakpoints.down('md')]: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

const SpeakerForm: FC = () => {

    const navigate = useNavigate();

    const {serviceStackClient} = useContext(FormsFeatureContext);

    const params = useParams();

    const registrationGuid = params?.guid;

    const {registration, loading, mutate} = useSpeakerByGuid(registrationGuid);  //registrationGuid);

    const initialFormValue = useMemo(() => ({
                                        [immerable]: true,
                                        ...registration,
                                        submittingCustomRegistration: true
                                    }), [registration]);

    const formMetadata = useFormMetadata(1);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, })

    const onSubmitValidationFailed = useFormSubmitValidationFailed();

    const { data: authUser } = useContext(AuthenticatedUserContext);
    const user = authUser as UserDto;

    //block this route from non speakers
    useEffect(() => {
        if (registration && !registration.isSpeaker){
            navigate('/registration-update/' + registration.guid);
        }
    }, [registration, navigate]);

    const validate: IFormValidate<Registration, IValidationError> = useFormValidate(formMetadata);
    
    const onSubmit:IFormSubmit<Registration, IValidationError> = async (formState) => {
        if (!formState.value) {
            return;
        }

        //biography upload
        const isBioUploaded = formState.dirty.speakerBio;

        // photo upload
        const isPhotoUploaded = formState.dirty.speakerPhoto;

        // slides upload
        const isSlidesUploaded = formState.dirty.speakerPresentations;

        // travel info upload
        const isTravelInfoUploaded = formState.dirty.planeTickets;

        try {
            await serviceStackClient.post(new SaveSpeaker({
                registration: formState.value
            }))
            .then(async (_) => {
                enqueueSnackbar('Speaker saved successfully', { variant: 'success' });
                // Reload the data
                await mutate();
            })
        }
        catch (err) {
            enqueueSnackbar('An error occurred while saving the speaker. Please try again later.', { variant: 'error' });
        }
        
        if (isBioUploaded || isPhotoUploaded || isSlidesUploaded || isTravelInfoUploaded) {
                await serviceStackClient.post(new SendSpeakerUploadedDetailsEmail({
                    firstName: formState.value.firstName,
                    lastName: formState.value.lastName,
                    biographyUploaded: isBioUploaded,
                    pictureUploaded: isPhotoUploaded,
                    slidesUploaded: isSlidesUploaded,
                    travelInformationUploaded: isTravelInfoUploaded
                }));
        }
    }

    return (
        <Box
        >
        {/* this will be a picture banner */}

            <BannerImage
                src={speakerBannerImg}
                alt="speaker banner"
            />

            <ContentBox>

                <Paper 
                    elevation={2} 
                    sx={{
                        padding:'40px', 
                        pb: 10, 
                        margin: 0,
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px"
                    }}>
                    {
                        loading && (
                            <>
                            <Loading/>
                            </>
                        )
                    }
                    {
                        (!loading && !registration) && (
                            <Alert severity="error" sx={{ width: '100%' }}>
                                <Typography>
                                    <b>No registration found</b> for this guid: <b>{registrationGuid}</b>. Please check the link you have received in your email.
                                </ Typography>
                            </Alert>
                        )
                    }
                    {
                        (registration && !loading) && (
                            <>
                                <Form
                                    initialValue={initialFormValue}
                                    debug={import.meta.env.DEV}
                                    lookups={lookups}
                                    onValidate={validate}
                                    validateOn={ValidateOn.onChange}
                                    onSubmit={onSubmit}
                                    onSubmitValidationFailed={onSubmitValidationFailed}
                                >
                                    <PackageProvider>
                                        <StyledTypography 
                                            variant="body1"
                                        >
                                            <a href='https://www.breastcancertrials.org.au/' color="primary">Breast Cancer Trials (BCT)</a> is a world-leading clinical trials research organisation, dedicated to finding new and better treatments and prevention strategies for people affected by this disease. We conduct a multi-center national and international clinical trials research program, which involves more than 1,000 researchers across 118 institutions throughout Australia and New Zealand.
                                        </StyledTypography>
                                        <StyledTypography variant="body1">
                                            This year is the 47th year of the conference, which will be held in Brisbane, Queensland from Tuesday 28th - Friday 31st July, followed by a <a href='https://bct2026.org/?page_id=2495' color='primary'>Trainee and Early Career Day</a> on Saturday 1st August. <a href='https://bct2026.org/' color='primary'>Click here</a> to view the official website for our 2026 Annual Scientific Meeting (ASM).
                                        </StyledTypography>
                                        
                                        {
                                            !registration?.isTAndECSpeaker && (
                                                <StyledTypography variant="body1">
                                                    As an ASM guest speaker BCT wish to invite you to the Q&A (Tuesday at 5:30pm), Faculty dinner (Wednesday at 7:00pm) and Conference dinner (Thursday at 7:00pm).
                                                </StyledTypography>
                                            )
                                        }
                                        
                                        {
                                            registration?.isTAndECSpeaker && (
                                                <StyledTypography variant="body1">
                                                    As a Trainee and Early Career Day guest speaker BCT wish to invite you to the Trainee and Early Career dinner (Friday at 6:00pm).
                                                </StyledTypography>
                                            )
                                        }
                                      
                                        <SpeakerSection/>
                                    
                                        <SpeakerGuideSection />
                                    
                                        <AdminContext.Provider value={user?.isAdmin ?? false}>
                                            <SpeakerTravelSection />
                                        </AdminContext.Provider>
                                    
                                        <AccommodationSectionUpdate />

                                        <SessionsSectionUpdate />

                                        <FormSubmitButton 
                                            variant="contained"
                                            sx={{
                                                float: "right",
                                            }}
                                            >
                                            Submit
                                        </FormSubmitButton>         
                                    </PackageProvider>
                                </Form>

                            </>
                        )
                    }

                </Paper>
            </ContentBox>

        </Box>
    )
}

export default SpeakerForm;
